import apiService from '../apiService';

// ========== 任務選項 ==========

/**
 * 獲取任務相關選項（獎勵類型、執行者類型、受眾類型、重複週期、屬性等）
 * @returns {Promise} API 響應
 */
export const getMissionOptions = async () => {
    const url = '/api/admin/mission-options';
    return apiService.GET(url);
};

// ========== 任務分類 ==========

/**
 * 獲取任務分類列表
 * @returns {Promise} API 響應
 */
export const getMissionCategories = async () => {
    const url = '/api/admin/mission-categories';
    return apiService.GET(url);
};

/**
 * 創建任務分類
 * @param {Object} categoryData - 分類資料
 * @param {string} categoryData.category - 分類代碼
 * @param {string} categoryData.name - 分類名稱
 * @returns {Promise} API 響應
 */
export const createMissionCategory = async (categoryData) => {
    const url = '/api/admin/mission-categories';
    return apiService.POST(url, categoryData);
};

/**
 * 更新任務分類
 * @param {number} categoryId - 分類 ID
 * @param {Object} categoryData - 分類資料
 * @param {string} categoryData.category - 分類代碼
 * @param {string} categoryData.name - 分類名稱
 * @returns {Promise} API 響應
 */
export const updateMissionCategory = async (categoryId, categoryData) => {
    const url = `/api/admin/mission-categories/${categoryId}`;
    return apiService.PUT(url, categoryData);
};

/**
 * 刪除任務分類
 * @param {number} categoryId - 分類 ID
 * @returns {Promise} API 響應
 */
export const deleteMissionCategory = async (categoryId) => {
    const url = `/api/admin/mission-categories/${categoryId}`;
    return apiService.DELETE(url);
};

// ========== 任務管理 ==========

/**
 * 獲取任務列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @returns {Promise} API 響應
 */
export const getMissions = async (params = {}) => {
    const url = '/api/admin/missions';
    return apiService.GET(url, params);
};

/**
 * 創建新任務
 * @param {Object} missionData - 任務資料
 * @param {string} missionData.title - 任務標題
 * @param {string} missionData.description - 任務描述
 * @returns {Promise} API 響應
 */
export const createMission = async (missionData) => {
    const url = '/api/admin/missions';
    return apiService.POST(url, missionData);
};

/**
 * 獲取單一任務詳情
 * @param {number} missionId - 任務 ID
 * @returns {Promise} API 響應
 */
export const getMissionDetail = async (missionId) => {
    const url = `/api/admin/missions/${missionId}`;
    return apiService.GET(url);
};

/**
 * 更新任務
 * @param {number} missionId - 任務 ID
 * @param {Object} missionData - 要更新的任務資料（所有欄位皆為選填）
 * @param {string} [missionData.name] - 任務名稱
 * @param {string} [missionData.description] - 任務描述
 * @param {number} [missionData.category_id] - 任務分類 ID
 * @param {number} [missionData.sequence] - 排序順序
 * @param {boolean} [missionData.is_active] - 是否啟用
 * @param {string} [missionData.attribute] - 任務屬性（如：neutral）
 * @param {boolean} [missionData.is_required] - 是否必填
 * @param {boolean} [missionData.is_repeatable] - 是否可重複
 * @param {string} [missionData.repeat_cycle] - 重複週期（如：daily）
 * @param {number} [missionData.max_times_per_cycle] - 每個週期最大次數
 * @param {number} [missionData.unlock_from_node_id] - 解鎖來源節點 ID
 * @param {string} [missionData.start_time] - 開始時間（格式：YYYY-MM-DD HH:mm:ss）
 * @param {string} [missionData.end_time] - 結束時間（格式：YYYY-MM-DD HH:mm:ss）
 * @param {boolean} [missionData.auto_complete] - 是否自動完成
 * @param {Array<Object>} [missionData.rewards] - 獎勵陣列
 * @param {string} [missionData.rewards[].type] - 獎勵類型（如：cp, pp）
 * @param {number} [missionData.rewards[].value] - 獎勵數值
 * @returns {Promise} API 響應
 */
export const updateMission = async (missionId, missionData) => {
    const url = `/api/admin/missions/${missionId}`;
    return apiService.PUT(url, missionData);
};

/**
 * 刪除任務
 * @param {number} missionId - 任務 ID
 * @returns {Promise} API 響應
 */
export const deleteMission = async (missionId) => {
    const url = `/api/admin/missions/${missionId}`;
    return apiService.DELETE(url);
};

/**
 * 交換節點排序（統一 API，支援 mission/chapter/task/step）
 * @param {string} type - 節點類型 ('mission', 'chapter', 'task', 'step')Ｆ
 * @param {number} firstId - 第一個節點 ID
 * @param {number} secondId - 第二個節點 ID
 * @returns {Promise} API 響應
 */
export const swapNodeSequence = async (type, firstId, secondId) => {
    const url = '/api/admin/nodes/swap-sequence';
    return apiService.POST(url, {
        type,
        first_id: firstId,
        second_id: secondId
    });
};

/**
 * 交換任務排序（已過時，請使用 swapNodeSequence）
 * @deprecated 請使用 swapNodeSequence('mission', firstMissionId, secondMissionId)
 */
export const swapMissionSequence = async (firstMissionId, secondMissionId) => {
    return swapNodeSequence('mission', firstMissionId, secondMissionId);
};

/**
 * 複製任務（包含所有章節、任務項目和步驟）
 * @param {number} missionId - 要複製的任務 ID
 * @param {Object} newData - 新任務的資料（可選，用於覆蓋名稱等）
 * @returns {Promise} API 響應
 */
export const duplicateMission = async (missionId, newData = {}) => {
    const url = `/api/admin/missions/${missionId}/duplicate`;
    return apiService.POST(url, newData);
};

/**
 * 複製任務/章節/任務項目/步驟（根據 ID 自動判斷層級）
 * @param {number} id - 要複製的項目 ID（可以是 mission、chapter、task 或 step 的 ID）
 * @returns {Promise} API 響應
 */
export const copyNode = async (id) => {
    const url = `/api/admin/missions/${id}/copy`;
    return apiService.POST(url);
};

// ========== 章節管理 ==========

/**
 * 獲取任務章節列表
 * @param {number} missionId - 任務 ID
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getMissionChapters = async (missionId, params = {}) => {
    const url = `/api/admin/missions/${missionId}/chapters`;
    return apiService.GET(url, params);
};

/**
 * 創建任務章節
 * @param {number} missionId - 任務 ID
 * @param {Object} chapterData - 章節資料
 * @param {string} chapterData.title - 章節標題
 * @param {string} chapterData.description - 章節描述
 * @returns {Promise} API 響應
 */
export const createMissionChapter = async (missionId, chapterData) => {
    const url = `/api/admin/missions/${missionId}/chapters`;
    return apiService.POST(url, chapterData);
};

/**
 * 獲取所有章節列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getChapters = async (params = {}) => {
    const url = '/api/admin/chapters';
    return apiService.GET(url, params);
};

/**
 * 創建新章節
 * @param {Object} chapterData - 章節資料
 * @returns {Promise} API 響應
 */
export const createChapter = async (chapterData) => {
    const url = '/api/admin/chapters';
    return apiService.POST(url, chapterData);
};

/**
 * 獲取單一章節詳情
 * @param {number} chapterId - 章節 ID
 * @returns {Promise} API 響應
 */
export const getChapterDetail = async (chapterId) => {
    const url = `/api/admin/chapters/${chapterId}`;
    return apiService.GET(url);
};

/**
 * 更新章節
 * @param {number} chapterId - 章節 ID
 * @param {Object} chapterData - 要更新的章節資料
 * @returns {Promise} API 響應
 */
export const updateChapter = async (chapterId, chapterData) => {
    const url = `/api/admin/chapters/${chapterId}`;
    return apiService.PUT(url, chapterData);
};

/**
 * 刪除章節
 * @param {number} chapterId - 章節 ID
 * @returns {Promise} API 響應
 */
export const deleteChapter = async (chapterId) => {
    const url = `/api/admin/chapters/${chapterId}`;
    return apiService.DELETE(url);
};

// ========== 任務項目管理 ==========

/**
 * 獲取章節任務項目列表
 * @param {number} chapterId - 章節 ID
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getChapterTasks = async (chapterId, params = {}) => {
    const url = `/api/admin/chapters/${chapterId}/tasks`;
    return apiService.GET(url, params);
};

/**
 * 創建章節任務項目
 * @param {number} chapterId - 章節 ID
 * @param {Object} taskData - 任務項目資料
 * @returns {Promise} API 響應
 */
export const createChapterTask = async (chapterId, taskData) => {
    const url = `/api/admin/chapters/${chapterId}/tasks`;
    return apiService.POST(url, taskData);
};

/**
 * 獲取所有任務項目列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getTasks = async (params = {}) => {
    const url = '/api/admin/tasks';
    return apiService.GET(url, params);
};

/**
 * 創建新任務項目
 * @param {Object} taskData - 任務項目資料
 * @returns {Promise} API 響應
 */
export const createTask = async (taskData) => {
    const url = '/api/admin/tasks';
    return apiService.POST(url, taskData);
};

/**
 * 獲取單一任務項目詳情
 * @param {number} taskId - 任務項目 ID
 * @returns {Promise} API 響應
 */
export const getTaskDetail = async (taskId) => {
    const url = `/api/admin/tasks/${taskId}`;
    return apiService.GET(url);
};

/**
 * 更新任務項目
 * @param {number} taskId - 任務項目 ID
 * @param {Object} taskData - 要更新的任務項目資料
 * @returns {Promise} API 響應
 */
export const updateTask = async (taskId, taskData) => {
    const url = `/api/admin/tasks/${taskId}`;
    return apiService.PUT(url, taskData);
};

/**
 * 刪除任務項目
 * @param {number} taskId - 任務項目 ID
 * @returns {Promise} API 響應
 */
export const deleteTask = async (taskId) => {
    const url = `/api/admin/tasks/${taskId}`;
    return apiService.DELETE(url);
};

// ========== 步驟管理 ==========

/**
 * 獲取任務項目步驟列表
 * @param {number} taskId - 任務項目 ID
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getTaskSteps = async (taskId, params = {}) => {
    const url = `/api/admin/tasks/${taskId}/steps`;
    return apiService.GET(url, params);
};

/**
 * 創建任務項目步驟
 * @param {number} taskId - 任務項目 ID
 * @param {Object} stepData - 步驟資料
 * @returns {Promise} API 響應
 */
export const createTaskStep = async (taskId, stepData) => {
    const url = `/api/admin/tasks/${taskId}/steps`;
    return apiService.POST(url, stepData);
};

/**
 * 獲取所有步驟列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getSteps = async (params = {}) => {
    const url = '/api/admin/steps';
    return apiService.GET(url, params);
};

/**
 * 創建新步驟
 * @param {Object} stepData - 步驟資料
 * @returns {Promise} API 響應
 */
export const createStep = async (stepData) => {
    const url = '/api/admin/steps';
    return apiService.POST(url, stepData);
};

/**
 * 獲取單一步驟詳情
 * @param {number} stepId - 步驟 ID
 * @returns {Promise} API 響應
 */
export const getStepDetail = async (stepId) => {
    const url = `/api/admin/steps/${stepId}`;
    return apiService.GET(url);
};

/**
 * 更新步驟
 * @param {number} stepId - 步驟 ID
 * @param {Object} stepData - 要更新的步驟資料
 * @returns {Promise} API 響應
 */
export const updateStep = async (stepId, stepData) => {
    const url = `/api/admin/steps/${stepId}`;
    return apiService.PUT(url, stepData);
};

/**
 * 刪除步驟
 * @param {number} stepId - 步驟 ID
 * @returns {Promise} API 響應
 */
export const deleteStep = async (stepId) => {
    const url = `/api/admin/steps/${stepId}`;
    return apiService.DELETE(url);
};

// ========== 節點階層查詢 ==========

/**
 * 獲取節點的完整階層路徑
 * @param {number} nodeId - 節點 ID（可以是 mission、chapter、task 或 step 的 ID）
 * @returns {Promise} API 響應，返回從 mission 到該節點的完整路徑陣列
 */
export const getNodeHierarchy = async (nodeId) => {
    const url = `/api/admin/mission-nodes/${nodeId}/hierarchy`;
    return apiService.GET(url);
};

// ========== 停啟用 API ==========

/**
 * 啟用任務
 * @param {number} missionId - 任務 ID
 * @returns {Promise} API 響應
 */
export const enableMission = async (missionId) => {
    const url = `/api/admin/missions/${missionId}/enable`;
    return apiService.PATCH(url);
};

/**
 * 停用任務
 * @param {number} missionId - 任務 ID
 * @returns {Promise} API 響應
 */
export const disableMission = async (missionId) => {
    const url = `/api/admin/missions/${missionId}/disable`;
    return apiService.PATCH(url);
};

/**
 * 啟用章節
 * @param {number} chapterId - 章節 ID
 * @returns {Promise} API 響應
 */
export const enableChapter = async (chapterId) => {
    const url = `/api/admin/chapters/${chapterId}/enable`;
    return apiService.PATCH(url);
};

/**
 * 停用章節
 * @param {number} chapterId - 章節 ID
 * @returns {Promise} API 響應
 */
export const disableChapter = async (chapterId) => {
    const url = `/api/admin/chapters/${chapterId}/disable`;
    return apiService.PATCH(url);
};

/**
 * 啟用任務項目
 * @param {number} taskId - 任務項目 ID
 * @returns {Promise} API 響應
 */
export const enableTask = async (taskId) => {
    const url = `/api/admin/tasks/${taskId}/enable`;
    return apiService.PATCH(url);
};

/**
 * 停用任務項目
 * @param {number} taskId - 任務項目 ID
 * @returns {Promise} API 響應
 */
export const disableTask = async (taskId) => {
    const url = `/api/admin/tasks/${taskId}/disable`;
    return apiService.PATCH(url);
};

/**
 * 啟用步驟
 * @param {number} stepId - 步驟 ID
 * @returns {Promise} API 響應
 */
export const enableStep = async (stepId) => {
    const url = `/api/admin/steps/${stepId}/enable`;
    return apiService.PATCH(url);
};

/**
 * 停用步驟
 * @param {number} stepId - 步驟 ID
 * @returns {Promise} API 響應
 */
export const disableStep = async (stepId) => {
    const url = `/api/admin/steps/${stepId}/disable`;
    return apiService.PATCH(url);
};

// ========== 條件管理 ==========

/**
 * 添加任務條件
 * @param {number} taskId - 任務項目 ID
 * @param {Object} conditionData - 條件資料
 * @returns {Promise} API 響應
 */
export const createTaskConditions = async (taskId, conditionData) => {
    const url = `/api/admin/tasks/${taskId}/conditions`;
    return apiService.POST(url, conditionData);
};

/**
 * 添加步驟條件
 * @param {number} stepId - 步驟 ID
 * @param {Object} conditionData - 條件資料
 * @returns {Promise} API 響應
 */
export const createStepConditions = async (stepId, conditionData) => {
    const url = `/api/admin/steps/${stepId}/conditions`;
    return apiService.POST(url, conditionData);
};

/**
 * 更新條件
 * @param {number} conditionId - 條件 ID
 * @param {Object} conditionData - 要更新的條件資料
 * @returns {Promise} API 響應
 */
export const updateCondition = async (conditionId, conditionData) => {
    const url = `/api/admin/conditions/${conditionId}`;
    return apiService.PUT(url, conditionData);
};

/**
 * 刪除條件
 * @param {number} conditionId - 條件 ID
 * @returns {Promise} API 響應
 */
export const deleteCondition = async (conditionId) => {
    const url = `/api/admin/conditions/${conditionId}`;
    return apiService.DELETE(url);
};

// ========== 條件審核管理 ==========

/**
 * 取得條件審核列表
 * @param {Object} params - 查詢參數
 * @param {number} [params.user_id] - 篩選特定使用者 ID
 * @param {string} [params.condition_type] - 篩選條件類型（purchase, form, quiz_pass 等）
 * @param {string} [params.result_status] - 篩選審核狀態（pending, approved, rejected, completed, failed, all）
 * @param {number} [params.mission_id] - 篩選特定主線 ID（包含所有下層節點）
 * @param {number} [params.chapter_id] - 篩選特定章節 ID（包含所有下層節點）
 * @param {number} [params.task_id] - 篩選特定任務 ID（包含所有下層節點）
 * @param {number} [params.step_id] - 篩選特定步驟 ID
 * @param {number} [params.rank_id] - 篩選聘階 ID
 * @param {number} [params.department_id] - 篩選事業部 ID
 * @param {number} [params.per_page] - 每頁筆數（預設 20，最大 100）
 * @param {number} [params.page] - 頁碼
 * @returns {Promise} API 響應
 */
export const getConditionReviews = async (params = {}) => {
    const url = '/api/admin/mission-conditions/reviews';
    return apiService.GET(url, params);
};

/**
 * 取得條件審核詳情
 * @param {number} id - 條件記錄 ID
 * @returns {Promise} API 響應
 */
export const getConditionReviewDetail = async (id) => {
    const url = `/api/admin/mission-conditions/reviews/${id}`;
    return apiService.GET(url);
};

/**
 * 審核通過條件記錄
 * @param {number} id - 條件記錄 ID
 * @param {Object} data - 審核資料
 * @param {string} [data.note] - 審核備註
 * @returns {Promise} API 響應
 */
export const approveConditionReview = async (id, data = {}) => {
    const url = `/api/admin/mission-conditions/${id}/approve`;
    return apiService.POST(url, data);
};

/**
 * 審核拒絕條件記錄
 * @param {number} id - 條件記錄 ID
 * @param {Object} data - 審核資料
 * @param {string} [data.note] - 審核備註
 * @returns {Promise} API 響應
 */
export const rejectConditionReview = async (id, data = {}) => {
    const url = `/api/admin/mission-conditions/${id}/reject`;
    return apiService.POST(url, data);
};

/**
 * 批次審核條件記錄
 * @param {Object} data - 批次審核資料
 * @param {Array<number>} data.condition_ids - 條件記錄 ID 陣列
 * @param {string} data.action - 審核動作（approve 或 reject）
 * @param {string} [data.note] - 審核備註
 * @returns {Promise} API 響應
 */
export const batchReviewConditions = async (data) => {
    const url = '/api/admin/mission-conditions/batch-review';
    return apiService.POST(url, data);
};

/**
 * 取得主線下拉選單
 * @returns {Promise} API 響應
 */
export const getMissionDropdown = async () => {
    const url = '/api/admin/mission-nodes/dropdown/missions';
    return apiService.GET(url);
};

/**
 * 取得章節下拉選單
 * @param {Object} params - 查詢參數
 * @param {number} [params.mission_id] - 篩選特定主線的章節
 * @returns {Promise} API 響應
 */
export const getChapterDropdown = async (params = {}) => {
    const url = '/api/admin/mission-nodes/dropdown/chapters';
    return apiService.GET(url, params);
};

/**
 * 取得任務下拉選單
 * @param {Object} params - 查詢參數
 * @param {number} [params.mission_id] - 篩選特定主線的任務
 * @param {number} [params.chapter_id] - 篩選特定章節的任務
 * @returns {Promise} API 響應
 */
export const getTaskDropdown = async (params = {}) => {
    const url = '/api/admin/mission-nodes/dropdown/tasks';
    return apiService.GET(url, params);
};

/**
 * 取得步驟下拉選單
 * @param {Object} params - 查詢參數
 * @param {number} [params.mission_id] - 篩選特定主線的步驟
 * @param {number} [params.chapter_id] - 篩選特定章節的步驟
 * @param {number} [params.task_id] - 篩選特定任務的步驟
 * @returns {Promise} API 響應
 */
export const getStepDropdown = async (params = {}) => {
    const url = '/api/admin/mission-nodes/dropdown/steps';
    return apiService.GET(url, params);
};

// ========== 任務進度記錄追蹤 ==========

/**
 * 取得所有任務進度記錄
 * @param {Object} params - 查詢參數
 * @param {number} [params.user_id] - 篩選特定使用者
 * @param {number} [params.node_id] - 篩選特定節點
 * @param {string} [params.node_type] - 篩選節點類型
 * @param {string} [params.status] - 篩選狀態
 * @param {number} [params.mission_id] - 篩選特定任務（階層查詢）
 * @param {number} [params.chapter_id] - 篩選特定章節（階層查詢）
 * @param {number} [params.task_id] - 篩選特定任務（階層查詢）
 * @param {number} [params.step_id] - 篩選特定步驟（階層查詢）
 * @param {string} [params.identity] - 篩選使用者身份：guest, student, partner, manager
 * @param {number} [params.rank_id] - 篩選聘階 ID
 * @param {number} [params.department_id] - 篩選事業部 ID
 * @param {string} [params.start_date] - 開始日期
 * @param {string} [params.end_date] - 結束日期
 * @param {number} [params.per_page] - 每頁筆數，預設 20
 * @param {number} [params.page] - 頁碼
 * @returns {Promise} API 響應
 */
export const getMissionProgress = async (params = {}) => {
    const url = '/api/admin/mission-progress';
    return apiService.GET(url, params);
};

/**
 * 取得任務樹統計
 * @param {Object} params - 查詢參數
 * @param {number} [params.mission_id] - 篩選特定任務（不傳則回傳所有任務）
 * @param {string} [params.identity] - 篩選使用者身份
 * @param {number} [params.rank_id] - 篩選聘階 ID
 * @param {number} [params.department_id] - 篩選事業部 ID
 * @param {string} [params.status] - 篩選狀態
 * @param {string} [params.start_date] - 開始日期
 * @param {string} [params.end_date] - 結束日期
 * @returns {Promise} API 響應
 */
export const getMissionTreeStats = async (params = {}) => {
    const url = '/api/admin/mission-progress/tree-stats';
    return apiService.GET(url, params);
};

/**
 * 取得節點執行者列表
 * @param {number} nodeId - 節點 ID
 * @param {Object} params - 查詢參數
 * @param {string} [params.status] - 篩選狀態
 * @param {string} [params.identity] - 篩選使用者身份
 * @param {number} [params.rank_id] - 篩選聘階 ID
 * @param {number} [params.department_id] - 篩選事業部 ID
 * @param {number} [params.per_page] - 每頁筆數，預設 20
 * @param {number} [params.page] - 頁碼
 * @returns {Promise} API 響應
 */
export const getNodeProgressUsers = async (nodeId, params = {}) => {
    const url = `/api/admin/mission-nodes/${nodeId}/progress/users`;
    return apiService.GET(url, params);
};

/**
 * 取得使用者任務進度列表（基本資訊）
 * @param {number} userId - 使用者 ID
 * @param {Object} params - 查詢參數
 * @param {number} [params.node_id] - 篩選特定節點
 * @param {string} [params.status] - 篩選狀態
 * @param {number} [params.per_page] - 每頁筆數，預設 20
 * @param {number} [params.page] - 頁碼
 * @returns {Promise} API 響應
 */
export const getUserMissionProgress = async (userId, params = {}) => {
    const url = `/api/admin/users/${userId}/mission-progress`;
    return apiService.GET(url, params);
};

/**
 * 取得使用者任務進度詳細資訊（完整資料）
 * @param {number} userId - 使用者 ID
 * @param {number} progressId - 進度記錄 ID
 * @returns {Promise} API 響應
 */
export const getUserMissionProgressDetails = async (userId, progressId) => {
    const url = `/api/admin/users/${userId}/mission-progress/${progressId}/details`;
    return apiService.GET(url);
};

/**
 * 編輯任務進度
 * @param {number} userId - 使用者 ID
 * @param {number} progressId - 進度記錄 ID
 * @param {Object} data - 編輯資料
 * @param {string} [data.status] - 狀態 (pending, in_progress, completed, failed, cancelled)
 * @param {string} [data.started_at] - 開始時間 (date-time 格式，可為 null)
 * @param {string} [data.completed_at] - 完成時間 (date-time 格式，可為 null)
 * @param {string} [data.note] - 操作備註 (最大 500 字元)
 * @returns {Promise} API 響應
 */
export const updateMissionProgress = async (userId, progressId, data = {}) => {
    const url = `/api/admin/users/${userId}/mission-progress/${progressId}`;
    return apiService.PUT(url, data);
};

/**
 * 取消任務進度
 * @param {number} userId - 使用者 ID
 * @param {number} progressId - 進度記錄 ID
 * @param {Object} data - 取消資料
 * @param {string} [data.note] - 取消原因 (最大 500 字元)
 * @returns {Promise} API 響應
 */
export const cancelMissionProgress = async (userId, progressId, data = {}) => {
    const url = `/api/admin/users/${userId}/mission-progress/${progressId}/cancel`;
    return apiService.POST(url, data);
};

/**
 * 重置任務進度
 * @param {number} userId - 使用者 ID
 * @param {number} progressId - 進度記錄 ID
 * @param {Object} data - 重置資料
 * @param {string} [data.reset_to] - 重置為的狀態 (pending 或 in_progress，預設 pending)
 * @param {boolean} [data.reset_conditions] - 是否重置條件記錄狀態 (預設 false)
 * @param {string} [data.note] - 重置原因 (最大 500 字元)
 * @returns {Promise} API 響應
 */
export const resetMissionProgress = async (userId, progressId, data = {}) => {
    const url = `/api/admin/users/${userId}/mission-progress/${progressId}/reset`;
    return apiService.POST(url, data);
};

/**
 * 刪除任務進度記錄
 * @param {number} userId - 使用者 ID
 * @param {number} progressId - 進度記錄 ID
 * @returns {Promise} API 響應
 */
export const deleteMissionProgress = async (userId, progressId) => {
    const url = `/api/admin/users/${userId}/mission-progress/${progressId}`;
    return apiService.DELETE(url);
};
